import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, CheckCircle2 } from "lucide-react";

/**
 * Reusable waitlist dialog.
 * Usage: <WaitlistDialog open={o} onOpenChange={setO} feature="AI portfolio balancing" title="…" />
 */
export default function WaitlistDialog({ open, onOpenChange, feature, title, blurb }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/waitlist", { email, feature });
      setDone(true);
      setCount(res.data.count_for_feature);
      toast.success(res.data.status === "already_on_list" ? "You're already on the list" : "You're on the list — thank you!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join waitlist");
    } finally { setLoading(false); }
  };

  const reset = (nextOpen) => {
    if (!nextOpen) {
      setDone(false); setEmail(""); setCount(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="sm:max-w-md" data-testid="waitlist-dialog">
        <DialogHeader>
          <div className="w-10 h-10 rounded-md bg-secondary/15 text-secondary grid place-items-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <DialogTitle className="serif text-2xl mt-2">{title || "Join the waitlist"}</DialogTitle>
          <DialogDescription>
            {blurb || `Get an email as soon as ${feature} ships. No spam — one email, one time.`}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <p className="serif text-xl mt-3">You&apos;re on the list.</p>
            {count !== null && (
              <p className="text-sm text-muted-foreground mt-1 mono">
                #{count} in line for <span className="text-foreground">{feature}</span>
              </p>
            )}
            <Button className="mt-6 rounded-full" onClick={() => reset(false)} data-testid="waitlist-close">
              Nice, thanks!
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="wl-email">Email</Label>
              <Input
                id="wl-email"
                data-testid="waitlist-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => reset(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="waitlist-submit" disabled={loading} className="rounded-full">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding…</> : "Notify me"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
